import { useState, useEffect, useRef } from 'react';
import { parseTimeControl } from '@/lib/clock-utils';

interface ClockState {
  whiteTimeMs: number;
  blackTimeMs: number;
  isRunning: boolean;
  activePlayer: 'white' | 'black';
}

export function useLocalClock(timeControl: string, currentTurn: 'white' | 'black', gameStatus: string, resetTrigger: number = 0) {
  const { timeMs, incrementMs } = parseTimeControl(timeControl);
  
  const [clockState, setClockState] = useState<ClockState>({
    whiteTimeMs: timeMs,
    blackTimeMs: timeMs,
    isRunning: false,
    activePlayer: 'white'
  });

  // Reset clock when resetTrigger changes (new game)
  useEffect(() => {
    if (resetTrigger > 0) {
      setClockState({
        whiteTimeMs: timeMs,
        blackTimeMs: timeMs,
        isRunning: false,
        activePlayer: 'white'
      });
    }
  }, [resetTrigger, timeMs]);

  const intervalRef = useRef<NodeJS.Timeout>();
  const lastTickRef = useRef<number>(Date.now());

  // Start/stop clock based on game status and turn
  useEffect(() => {
    const shouldRun = gameStatus === 'ongoing' || gameStatus === 'check';
    
    if (shouldRun && !clockState.isRunning) {
      lastTickRef.current = Date.now();
      setClockState(prev => ({ ...prev, isRunning: true, activePlayer: currentTurn }));
    } else if (!shouldRun && clockState.isRunning) {
      setClockState(prev => ({ ...prev, isRunning: false }));
    } else if (clockState.isRunning && clockState.activePlayer !== currentTurn) {
      // Add increment to the player who just moved
      setClockState(prev => ({
        ...prev,
        activePlayer: currentTurn,
        [prev.activePlayer === 'white' ? 'whiteTimeMs' : 'blackTimeMs']: 
          prev.activePlayer === 'white' 
            ? Math.max(0, prev.whiteTimeMs + incrementMs)
            : Math.max(0, prev.blackTimeMs + incrementMs)
      }));
      lastTickRef.current = Date.now();
    }
  }, [gameStatus, currentTurn, clockState.isRunning, clockState.activePlayer, incrementMs]);

  // Clock tick
  useEffect(() => {
    if (!clockState.isRunning) {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = undefined;
      }
      return;
    }

    intervalRef.current = setInterval(() => {
      const now = Date.now();
      const elapsed = now - lastTickRef.current;
      lastTickRef.current = now;

      setClockState(prev => {
        const activeTimeKey = prev.activePlayer === 'white' ? 'whiteTimeMs' : 'blackTimeMs';
        const newTime = Math.max(0, prev[activeTimeKey] - elapsed);
        
        return {
          ...prev,
          [activeTimeKey]: newTime
        };
      });
    }, 100);

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, [clockState.isRunning]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, []);

  const isTimeUp = clockState.whiteTimeMs <= 0 || clockState.blackTimeMs <= 0;

  return {
    whiteTimeMs: clockState.whiteTimeMs,
    blackTimeMs: clockState.blackTimeMs,
    isTimeUp,
    activePlayer: clockState.activePlayer
  };
}